import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  getDateMomentObjectCustom,
  getISOString,
  formatDate,
} from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

// Action Types
const ACTION_TYPES = {
  UPDATE_DATE: "UPDATE_DATE",
  UPDATE_TIME: "UPDATE_TIME",
  UPDATE_DATETIME: "UPDATE_DATETIME",
  SET_VALIDATION_ERROR: "SET_VALIDATION_ERROR",
  CLEAR_VALIDATION_ERROR: "CLEAR_VALIDATION_ERROR",
  RESET_STATE: "RESET_STATE",
};

/**
 * Initial state for date/time fields
 */
const createInitialState = ({
  openDate = "",
  dueDate = "",
  closeDate = "",
}) => ({
  dates: {
    openDate,
    dueDate,
    closeDate,
  },
  validationErrors: {
    openDate: false,
    dueDate: false,
    closeDate: false,
  },
});

/**
 * Reducer function to manage date/time field state
 */
const dateTimeFieldsReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.UPDATE_DATE: {
      const { field, newDate } = action.payload;
      const currentDateTime = state.dates[field];

      if (_.isNil(newDate) || _.isEmpty(currentDateTime)) {
        return state;
      }

      // Extract current time from existing dateTime
      const currentTime = formatDate({
        date: currentDateTime,
        format: DATE_TIME_CONSTANTS.HH_mm,
      });

      // Combine new date with existing time
      const newDateTime = getISOString(
        getDateMomentObjectCustom(
          `${formatDate({
            date: newDate,
            format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
          })}:${currentTime}`,
          DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
        )
      );

      return {
        ...state,
        dates: {
          ...state.dates,
          [field]: newDateTime,
        },
      };
    }

    case ACTION_TYPES.UPDATE_TIME: {
      const { field, newTime } = action.payload;
      const currentDateTime = state.dates[field];

      if (_.isNil(newTime) || _.isEmpty(currentDateTime)) {
        return state;
      }

      // Extract current date from existing dateTime
      const currentDate = formatDate({
        date: currentDateTime,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      });

      // Combine existing date with new time
      const newDateTime = getISOString(
        getDateMomentObjectCustom(
          `${currentDate}:${newTime}`,
          DATE_TIME_CONSTANTS.YYYY_MM_DD__HH_mm
        )
      );
      return {
        ...state,
        dates: {
          ...state.dates,
          [field]: newDateTime,
        },
      };
    }

    case ACTION_TYPES.UPDATE_DATETIME: {
      const { field, dateTime } = action.payload;

      return {
        ...state,
        dates: {
          ...state.dates,
          [field]: dateTime,
        },
      };
    }

    case ACTION_TYPES.SET_VALIDATION_ERROR: {
      const { field, isInvalid } = action.payload;

      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [field]: isInvalid,
        },
      };
    }

    case ACTION_TYPES.CLEAR_VALIDATION_ERROR: {
      const { field } = action.payload;

      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [field]: false,
        },
      };
    }

    case ACTION_TYPES.RESET_STATE: {
      return createInitialState(action.payload);
    }

    default:
      return state;
  }
};

// Create Context
const DateTimeFieldsContext = createContext(undefined);

/**
 * Provider component for date/time fields state management
 * Manages all date/time fields with useReducer and provides actions via Context API
 */
export const DateTimeFieldsProvider = ({
  children,
  initialDates,
  onChange,
}) => {
  const [state, dispatch] = useReducer(
    dateTimeFieldsReducer,
    initialDates,
    createInitialState
  );

  // Re-sync reducer state when the consumer passes a different event's dates
  // (e.g. user clicks another event tile while the modal stays mounted).
  // useReducer's lazy init only runs on first mount, so without this the
  // reducer would keep showing the first event's dates indefinitely.
  const { openDate, dueDate, closeDate } = initialDates || {};
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    dispatch({
      type: ACTION_TYPES.RESET_STATE,
      payload: { openDate, dueDate, closeDate },
    });
  }, [openDate, dueDate, closeDate]);

  /**
   * Updates date for a specific field
   */
  const updateDate = useCallback((field, newDate) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_DATE,
      payload: { field, newDate },
    });
  }, []);

  /**
   * Updates time for a specific field
   */
  const updateTime = useCallback((field, newTime) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_TIME,
      payload: { field, newTime },
    });
  }, []);

  /**
   * Sets validation error for a specific field
   */
  const setValidationError = useCallback((field, isInvalid) => {
    dispatch({
      type: ACTION_TYPES.SET_VALIDATION_ERROR,
      payload: { field, isInvalid },
    });
  }, []);

  /**
   * Clears validation error for a specific field
   */
  const clearValidationError = useCallback(field => {
    dispatch({
      type: ACTION_TYPES.CLEAR_VALIDATION_ERROR,
      payload: { field },
    });
  }, []);

  /**
   * Resets entire state with new dates
   */
  const resetState = useCallback(newDates => {
    dispatch({
      type: ACTION_TYPES.RESET_STATE,
      payload: newDates,
    });
  }, []);

  /**
   * Check if there are any validation errors
   */
  const hasValidationErrors = _.some(state.validationErrors, error => error);

  const contextValue = {
    // State
    dates: state.dates,
    validationErrors: state.validationErrors,
    hasValidationErrors,

    // Actions
    updateDate,
    updateTime,
    setValidationError,
    clearValidationError,
    resetState,

    // onChange callback passed from parent
    onChange,
  };

  return (
    <DateTimeFieldsContext.Provider value={contextValue}>
      {children}
    </DateTimeFieldsContext.Provider>
  );
};

DateTimeFieldsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialDates: PropTypes.shape({
    openDate: PropTypes.string,
    dueDate: PropTypes.string,
    closeDate: PropTypes.string,
  }),
  onChange: PropTypes.func,
};

DateTimeFieldsProvider.defaultProps = {
  initialDates: {
    openDate: "",
    dueDate: "",
    closeDate: "",
  },
  onChange: null,
};

/**
 * Custom hook to use DateTimeFields context
 * Must be used within DateTimeFieldsProvider
 */
export const useDateTimeFieldsContext = () => {
  const context = useContext(DateTimeFieldsContext);

  if (context === undefined) {
    throw new Error(
      "useDateTimeFieldsContext must be used within a DateTimeFieldsProvider"
    );
  }

  return context;
};

export { ACTION_TYPES };
